class MultiCourierTracker {
  constructor() {
    // Use direct API URL since app proxy is not configured
    this.apiUrl = 'https://postex-tracking-api.vercel.app/api/track';
    this.initializeTracking();
  }

  initializeTracking() {
    document.addEventListener('DOMContentLoaded', () => {
      this.initializeFormSubmission();
      this.initializeAutoTracking();
      this.testApiConnection(); // Add API connection test
    });
  }

  // JSONP implementation to avoid CORS
  jsonpRequest(url, callback) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const callbackName = 'jsonpCallback_' + Math.random().toString(36).substr(2, 9);
      
      // Create global callback function
      window[callbackName] = (data) => {
        resolve(data);
        document.head.removeChild(script);
        delete window[callbackName];
      };
      
      // Set up error handling
      script.onerror = () => {
        reject(new Error('JSONP request failed'));
        document.head.removeChild(script);
        delete window[callbackName];
      };
      
      // Add callback parameter to URL
      const separator = url.includes('?') ? '&' : '?';
      script.src = `${url}${separator}callback=${callbackName}`;
      
      // Add script to page
      document.head.appendChild(script);
      
      // Timeout after 10 seconds
      setTimeout(() => {
        if (window[callbackName]) {
          reject(new Error('JSONP request timeout'));
          document.head.removeChild(script);
          delete window[callbackName];
        }
      }, 10000);
    });
  }

  // Fallback fetch with no-cors mode
  async fallbackFetch(url) {
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-cache'
      });
      
      // With no-cors, we can't read the response, so we'll return a mock response
      // indicating that the request was made but we can't get the data
      return {
        success: false,
        error: 'CORS blocked - using fallback mode',
        message: 'Request sent but response blocked by browser security'
      };
    } catch (error) {
      throw new Error('Fallback fetch also failed: ' + error.message);
    }
  }

  // Combined request method with fallback
  async makeRequest(url) {
    try {
      console.log('Trying JSONP request...');
      return await this.jsonpRequest(url);
    } catch (jsonpError) {
      console.log('JSONP failed, trying fallback fetch...', jsonpError);
      try {
        return await this.fallbackFetch(url);
      } catch (fetchError) {
        console.log('Fallback fetch also failed...', fetchError);
        throw new Error('All request methods failed');
      }
    }
  }

  async testApiConnection() {
    console.log('=== API CONNECTION TEST ===');
    const testUrl = this.apiUrl.replace('/track', '/test');
    console.log('Testing API connection to:', testUrl);
    
    try {
      const data = await this.makeRequest(testUrl);
      console.log('Test API working:', data);
    } catch (error) {
      console.error('Test API connection failed:', error);
      console.error('This indicates a network or API issue');
    }
    
    console.log('=== END API CONNECTION TEST ===');
  }

  initializeFormSubmission() {
    const form = document.getElementById('postex-tracking-form') || document.getElementById('courier-tracking-form');
    if (!form) return;

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = document.getElementById('trackingNumber');
      const trackingNumber = input.value.trim();

      if (trackingNumber) {
        this.trackOrder(trackingNumber);
      }
    });
  }

  getUrlParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  updateUrlWithTracking(trackingNumber) {
    const newUrl = `${window.location.pathname}?cn=${encodeURIComponent(trackingNumber)}`;
    window.history.pushState({ cn: trackingNumber }, '', newUrl);
  }

  initializeAutoTracking() {
    const container = document.getElementById('postex-tracking-container') || document.getElementById('courier-tracking-container');
    
    // First check URL parameter (?cn=)
    const urlTrackingNumber = this.getUrlParameter('cn');
    if (urlTrackingNumber) {
      const input = document.getElementById('trackingNumber');
      if (input) {
        input.value = urlTrackingNumber;
      }
      this.trackOrder(urlTrackingNumber, true);
      return;
    }
    
    // Then check data attribute (for order confirmation pages)
    if (!container) return;
    const trackingNumber = container.dataset.trackingNumber;
    if (trackingNumber) {
      this.trackOrder(trackingNumber, true);
    }
  }

  async trackOrder(trackingNumber, isAutomatic = false) {
    console.log('=== CLIENT DEBUG ===');
    console.log('Tracking Number:', trackingNumber);
    console.log('API URL:', this.apiUrl);
    console.log('Is Automatic:', isAutomatic);
    console.log('Current Origin:', window.location.origin);

    // Update URL with tracking number for shareable links (only if manual search)
    if (!isAutomatic) {
      this.updateUrlWithTracking(trackingNumber);
    }

    const loadingElement = document.querySelector('.postex-tracking__loading') || document.querySelector('.courier-tracking__loading');
    const contentElement = document.getElementById('postex-tracking-content') || document.getElementById('courier-tracking-content');
    const errorElement = document.getElementById('postex-tracking-error') || document.getElementById('courier-tracking-error');
    const buttonLoading = document.querySelector('.tracking-search__button-loading');
    const buttonText = document.querySelector('.tracking-search__button-text');

    this.showLoading(loadingElement, buttonLoading, buttonText, isAutomatic);
    this.hideElements([contentElement, errorElement]);

    const fullUrl = `${this.apiUrl}/${encodeURIComponent(trackingNumber)}`;
    console.log('Full URL:', fullUrl);

    try {
      console.log('Making request with fallback...');
      
      const data = await this.makeRequest(fullUrl);
      console.log('Response data:', data);

      if (data.success) {
        this.displayTrackingData(data.data, data.courier, contentElement);
        this.hideLoading(loadingElement, buttonLoading, buttonText, isAutomatic);
      } else {
        throw new Error(data.message || 'Order not found');
      }
    } catch (error) {
      console.error('=== ERROR DEBUG ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Full error object:', error);
      
      this.displayError('We couldn\'t find an order with that tracking number. Check the number and try again, or get in touch and we\'ll look it up for you.', errorElement);
      this.hideLoading(loadingElement, buttonLoading, buttonText, isAutomatic);
    }
    
    console.log('=== END CLIENT DEBUG ===');
  }

  displayTrackingData(data, courier, contentElement) {
    const progressPercentage = Math.max(0, Math.min(100, data.progressPercentage || 0));
    
    // Courier name plus a modifier class. Colour deliberately does NOT travel
    // in an inline style any more: it lives in order-tracking.css, so the
    // palette stays in one place and an API-supplied value never reaches a
    // style attribute.
    const getCourierInfo = (courierType) => {
      switch (courierType) {
        case 'postex':
          return { name: 'PostEx', modifier: 'courier-info-banner--postex' };
        case 'leopards':
          return { name: 'Leopards Courier', modifier: 'courier-info-banner--leopards' };
        default:
          return { name: 'Courier', modifier: '' };
      }
    };

    const courierInfo = getCourierInfo(courier);

    // Status marker modifier. An unrecognised status returns '' and the dot
    // falls back to its neutral colour, rather than disappearing.
    const getStatusModifier = (status) => {
      const statusLower = (status || '').toLowerCase();
      if (statusLower.includes('delivered') || statusLower.includes('completed')) return 'postex-status-icon--delivered';
      if (statusLower.includes('out for delivery') || statusLower.includes('in transit')) return 'postex-status-icon--transit';
      if (statusLower.includes('processing') || statusLower.includes('confirmed')) return 'postex-status-icon--processing';
      if (statusLower.includes('shipped') || statusLower.includes('dispatched')) return 'postex-status-icon--transit';
      if (statusLower.includes('returned') || statusLower.includes('refunded')) return 'postex-status-icon--returned';
      return '';
    };

    const statusModifier = getStatusModifier(data.currentStatus?.message);

    contentElement.innerHTML = `
      <div class="postex-tracking__content-inner">
        <div class="courier-info-banner ${courierInfo.modifier}">
          <div class="courier-info-content">
            <span class="courier-name">${courierInfo.name}</span>
            <span class="courier-label">Courier</span>
          </div>
        </div>

        <div class="postex-status-hero">
          <div class="postex-status-icon ${statusModifier}" aria-hidden="true"></div>
          <div class="postex-status-info">
            <h3 class="postex-status-title">${this.escapeHtml(data.currentStatus?.message || 'Order status')}</h3>
            <p class="postex-status-description">${this.escapeHtml(data.currentStatus?.description || 'Your order is on its way.')}</p>
          </div>
        </div>

        <div class="postex-progress-section">
          <div class="postex-progress-bar">
            <div class="postex-progress-fill" style="width: ${progressPercentage}%"></div>
          </div>
          <div class="postex-progress-text">${progressPercentage}% Complete</div>
        </div>

        <div class="postex-cards-grid">
          <div class="postex-info-card">
            <h4 class="postex-card-title">Order Details</h4>
            <div class="postex-card-content">
              <div class="postex-detail-row">
                <span class="postex-detail-label">Order Reference:</span>
                <span class="postex-detail-value">${this.escapeHtml(data.orderReference || data.trackingNumber)}</span>
              </div>
              <div class="postex-detail-row">
                <span class="postex-detail-label">Amount:</span>
                <span class="postex-detail-value">PKR ${(data.orderDetails?.amount || 0).toLocaleString()}</span>
              </div>
              <div class="postex-detail-row">
                <span class="postex-detail-label">Items:</span>
                <span class="postex-detail-value">${data.orderDetails?.items || 1}</span>
              </div>
              <div class="postex-detail-row">
                <span class="postex-detail-label">Order Date:</span>
                <span class="postex-detail-value">${this.formatDate(data.orderDetails?.orderDate)}</span>
              </div>
            </div>
          </div>

          <!--
            Deliberately NOT shown here: customer name, phone number, full
            street address and the order's special instructions.

            This page needs no login — a tracking number is the only key — and
            PostEx numbers run in sequence, so anyone could count upward through
            them. Printing those four fields would have turned the page into a
            way to harvest customers' names, phone numbers and home addresses.

            City stays because it is not identifying on its own and it lets a
            customer confirm at a glance that they are looking at their own
            parcel. Anyone tracking their own delivery already knows the rest.
          -->
          <div class="postex-info-card">
            <h4 class="postex-card-title">Delivery</h4>
            <div class="postex-card-content">
              <div class="postex-detail-row">
                <span class="postex-detail-label">Destination:</span>
                <span class="postex-detail-value">${this.escapeHtml(data.customer?.city || 'Pakistan')}</span>
              </div>
              <div class="postex-detail-row">
                <span class="postex-detail-label">Tracking number:</span>
                <span class="postex-detail-value">${this.escapeHtml(data.trackingNumber || '')}</span>
              </div>
            </div>
          </div>
        </div>

        ${data.statusHistory && data.statusHistory.length > 0 ? `
          <div class="postex-timeline-section">
            <h4 class="postex-section-title">Order Timeline</h4>
            <div class="postex-timeline">
              ${data.statusHistory.map(status => {
                return `
                  <div class="postex-timeline-item">
                    <div class="postex-timeline-icon" aria-hidden="true"></div>
                    <div class="postex-timeline-content">
                      <h5 class="postex-timeline-title">${this.escapeHtml(status.status || '')}</h5>
                      <p class="postex-timeline-description">${this.escapeHtml(status.description || '')}</p>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        ` : ''}

        <div class="postex-actions">
          <div class="postex-status-indicators">
            ${data.isDelivered ? '<span class="postex-badge postex-badge--success">Delivered</span>' : ''}
            ${data.isInProgress ? '<span class="postex-badge postex-badge--info">In Progress</span>' : ''}
            ${data.isReturned ? '<span class="postex-badge postex-badge--warning">Returned</span>' : ''}
          </div>
        </div>
      </div>
    `;

    contentElement.style.display = 'block';
  }

  displayError(message, errorElement) {
    const errorCard = errorElement.querySelector('.postex-error-card');
    if (errorCard) {
      const messageEl = errorCard.querySelector('.postex-error-message');
      if (messageEl) {
        messageEl.textContent = message || 'Unable to load tracking information';
      }
    }
    errorElement.style.display = 'block';
  }

  showLoading(loadingElement, buttonLoading, buttonText, isAutomatic) {
    if (loadingElement && isAutomatic) {
      loadingElement.style.display = 'block';
    }
    if (buttonLoading && buttonText && !isAutomatic) {
      buttonLoading.style.display = 'flex';
      buttonText.style.display = 'none';
    }
  }

  hideLoading(loadingElement, buttonLoading, buttonText, isAutomatic) {
    if (loadingElement && isAutomatic) {
      loadingElement.style.display = 'none';
    }
    if (buttonLoading && buttonText && !isAutomatic) {
      buttonLoading.style.display = 'none';
      buttonText.style.display = 'block';
    }
  }

  hideElements(elements) {
    elements.forEach(element => {
      if (element) element.style.display = 'none';
    });
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  formatDate(dateString) {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }
}

// Initialize tracker when script loads
new MultiCourierTracker();